/**
 * Chart Data Builders
 *
 * Builds chart-ready data series for all 8 output charts from
 * normalized output records and daily aggregates.
 *
 * Charts produced:
 * 1. Daily Stool Count (bar)
 * 2. Daily Urine Count (bar)
 * 3. Stool Shape Scatter (Bristol scale)
 * 4. Stool Size Scatter (amount score)
 * 5. Stool Color Stacked Bar
 * 6. Urine Color Line (daily max)
 * 7. Urine Type Histogram
 * 8. Urine Color Stacked Bar
 */

import moment from 'moment';

import type {
  NormalizedOutputRecord,
  DailyOutputAggregate,
  ChartDataSeries,
  ScatterDataSeries,
  StackedBarDataSeries,
  HistogramDataSeries,
} from '../types';

import { decodeStoolColor, decodeUrineColor } from '../pipeline/codeDecoder';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Generate day labels for all days in a given month.
 * Returns ["1", "2", ..., "28/29/30/31"] depending on the month.
 */
function generateDayLabels(year: number, month: number): string[] {
  const daysInMonth = moment({ year, month: month - 1 }).daysInMonth();
  return Array.from({ length: daysInMonth }, (_, i) => String(i + 1));
}

/**
 * Build a map from dateKey (YYYY-MM-DD) to day index (0-based) for quick lookup.
 */
function buildDateKeyToIndexMap(year: number, month: number): Map<string, number> {
  const daysInMonth = moment({ year, month: month - 1 }).daysInMonth();
  const map = new Map<string, number>();
  for (let day = 1; day <= daysInMonth; day++) {
    const dateKey = moment({ year, month: month - 1, day }).format('YYYY-MM-DD');
    map.set(dateKey, day - 1);
  }
  return map;
}

// ─── Chart Builders ──────────────────────────────────────────────────────────

/**
 * Build daily stool count bar chart.
 * One bar per day of month, value = stoolCount for that day.
 * Days with no data get 0.
 */
export function buildDailyStoolCountChart(
  dailyAggregates: DailyOutputAggregate[],
  year: number,
  month: number
): ChartDataSeries {
  const labels = generateDayLabels(year, month);
  const dateKeyToIndex = buildDateKeyToIndexMap(year, month);
  const data = new Array<number>(labels.length).fill(0);
  const tooltipNotes: string[] = new Array(labels.length).fill('');

  for (const agg of dailyAggregates) {
    const idx = dateKeyToIndex.get(agg.dateKey);
    if (idx !== undefined) {
      data[idx] = agg.stoolCount;
      // Include tooltip notes if available
      if (agg.notes.length > 0) {
        tooltipNotes[idx] = agg.notes.map(n => n.noteText).join('; ');
      }
    }
  }

  const hasTooltips = tooltipNotes.some(t => t !== '');

  return {
    labels,
    datasets: [
      {
        label: 'Daily Stool Count',
        data,
        ...(hasTooltips ? { tooltipNotes } : {}),
      },
    ],
  };
}

/**
 * Build daily urine count bar chart.
 * One bar per day of month, value = urineCount for that day.
 * Days with no data get 0.
 */
export function buildDailyUrineCountChart(
  dailyAggregates: DailyOutputAggregate[],
  year: number,
  month: number
): ChartDataSeries {
  const labels = generateDayLabels(year, month);
  const dateKeyToIndex = buildDateKeyToIndexMap(year, month);
  const data = new Array<number>(labels.length).fill(0);
  const tooltipNotes: string[] = new Array(labels.length).fill('');

  for (const agg of dailyAggregates) {
    const idx = dateKeyToIndex.get(agg.dateKey);
    if (idx !== undefined) {
      data[idx] = agg.urineCount;
      if (agg.notes.length > 0) {
        tooltipNotes[idx] = agg.notes.map(n => n.noteText).join('; ');
      }
    }
  }

  const hasTooltips = tooltipNotes.some(t => t !== '');

  return {
    labels,
    datasets: [
      {
        label: 'Daily Urine Count',
        data,
        ...(hasTooltips ? { tooltipNotes } : {}),
      },
    ],
  };
}

/**
 * Build stool shape scatter chart.
 * x = dateKey, y = patternCode (Bristol 1-7).
 * Only include records with patternCode > 0.
 * Multiple dots per day (no averaging).
 */
export function buildStoolShapeScatter(
  stoolRecords: NormalizedOutputRecord[]
): ScatterDataSeries {
  const data = stoolRecords
    .filter(r => r.patternCode > 0)
    .map(r => ({
      x: r.dateKey,
      y: r.patternCode,
      tooltip: r.patternLabel,
    }));

  return {
    datasets: [
      {
        label: 'Stool Shape (Bristol Scale)',
        data,
      },
    ],
  };
}

/**
 * Build stool size scatter chart.
 * x = dateKey, y = amountScore (1-3).
 * All stool records included.
 */
export function buildStoolSizeScatter(
  stoolRecords: NormalizedOutputRecord[]
): ScatterDataSeries {
  const data = stoolRecords.map(r => ({
    x: r.dateKey,
    y: r.amountScore,
    tooltip: r.amountLabel,
  }));

  return {
    datasets: [
      {
        label: 'Stool Size (Amount Score)',
        data,
      },
    ],
  };
}

/**
 * Build stool color stacked bar chart.
 * One stacked bar per day, segments = count of each stool color.
 * Datasets: one per color code (1-9), with backgroundColor from decodeStoolColor hex.
 */
export function buildStoolColorStacked(
  dailyAggregates: DailyOutputAggregate[],
  stoolRecords: NormalizedOutputRecord[],
  year: number,
  month: number
): StackedBarDataSeries {
  const labels = generateDayLabels(year, month);
  const dateKeyToIndex = buildDateKeyToIndexMap(year, month);

  // Group stool records by dateKey and colorCode
  const dayColorCounts = new Map<string, Map<number, number>>();
  for (const record of stoolRecords) {
    if (!dayColorCounts.has(record.dateKey)) {
      dayColorCounts.set(record.dateKey, new Map());
    }
    const colorMap = dayColorCounts.get(record.dateKey)!;
    colorMap.set(record.colorCode, (colorMap.get(record.colorCode) || 0) + 1);
  }

  // Build one dataset per stool color code (1-9)
  const datasets: StackedBarDataSeries['datasets'] = [];
  for (let colorCode = 1; colorCode <= 9; colorCode++) {
    const { label, hex } = decodeStoolColor(colorCode);
    const data = new Array<number>(labels.length).fill(0);

    for (const [dateKey, colorMap] of dayColorCounts) {
      const idx = dateKeyToIndex.get(dateKey);
      if (idx !== undefined) {
        data[idx] = colorMap.get(colorCode) || 0;
      }
    }

    datasets.push({
      label,
      data,
      backgroundColor: hex,
    });
  }

  return { labels, datasets };
}

/**
 * Build urine color line chart.
 * One point per day, value = max(colorCode) among that day's urine records.
 * Days with no urine records get 0.
 */
export function buildUrineColorLine(
  dailyAggregates: DailyOutputAggregate[],
  year: number,
  month: number
): ChartDataSeries {
  const labels = generateDayLabels(year, month);
  const dateKeyToIndex = buildDateKeyToIndexMap(year, month);
  const data = new Array<number>(labels.length).fill(0);

  for (const agg of dailyAggregates) {
    const idx = dateKeyToIndex.get(agg.dateKey);
    if (idx !== undefined && agg.urineColorEvents.length > 0) {
      // Use max colorCode for worst-case
      data[idx] = Math.max(...agg.urineColorEvents.map(e => e.colorCode));
    }
  }

  return {
    labels,
    datasets: [
      {
        label: 'Urine Color (Daily Max)',
        data,
      },
    ],
  };
}

/**
 * Build urine type histogram.
 * Labels = ["Normal", "Diaper", "CBD", "Urostomy"], data = count per type.
 */
export function buildUrineTypeHistogram(
  urineRecords: NormalizedOutputRecord[]
): HistogramDataSeries {
  const typeLabels = ['Normal', 'Diaper', 'CBD', 'Urostomy'];
  const counts = new Map<string, number>();

  for (const label of typeLabels) {
    counts.set(label, 0);
  }

  for (const record of urineRecords) {
    const current = counts.get(record.typeLabel) || 0;
    counts.set(record.typeLabel, current + 1);
  }

  return {
    labels: typeLabels,
    data: typeLabels.map(label => counts.get(label) || 0),
  };
}

/**
 * Build urine color stacked bar chart.
 * Same pattern as stool color stacked but for urine colors (1-7).
 * Uses decodeUrineColor for hex values.
 */
export function buildUrineColorStacked(
  dailyAggregates: DailyOutputAggregate[],
  urineRecords: NormalizedOutputRecord[],
  year: number,
  month: number
): StackedBarDataSeries {
  const labels = generateDayLabels(year, month);
  const dateKeyToIndex = buildDateKeyToIndexMap(year, month);

  // Group urine records by dateKey and colorCode
  const dayColorCounts = new Map<string, Map<number, number>>();
  for (const record of urineRecords) {
    if (!dayColorCounts.has(record.dateKey)) {
      dayColorCounts.set(record.dateKey, new Map());
    }
    const colorMap = dayColorCounts.get(record.dateKey)!;
    colorMap.set(record.colorCode, (colorMap.get(record.colorCode) || 0) + 1);
  }

  // Build one dataset per urine color code (1-7)
  const datasets: StackedBarDataSeries['datasets'] = [];
  for (let colorCode = 1; colorCode <= 7; colorCode++) {
    const { label, hex } = decodeUrineColor(colorCode);
    const data = new Array<number>(labels.length).fill(0);

    for (const [dateKey, colorMap] of dayColorCounts) {
      const idx = dateKeyToIndex.get(dateKey);
      if (idx !== undefined) {
        data[idx] = colorMap.get(colorCode) || 0;
      }
    }

    datasets.push({
      label,
      data,
      backgroundColor: hex,
    });
  }

  return { labels, datasets };
}
